import { useState, useMemo } from 'react';
import {
  Plus,
  Search,
  MoreHorizontal,
  Users,
  Building2,
  Mail,
  Phone,
  Archive,
  Trash2,
  Pencil,
  Briefcase,
  Download,
  Upload,
  Sparkles,
  Copy,
  X,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { useContacts } from '@/lib/hooks/leads/useContacts';
import { useCompanies } from '@/lib/hooks/leads/useCompanies';
import { useImportExport } from '@/lib/hooks/leads/useImportExport';
import { useLeadsAI } from '@/lib/hooks/leads/useAI';
import { useBulkActions } from '@/lib/hooks/leads/useBulkActions';
import type { LeadsContact, LeadsCreateContactInput } from '@/lib/api';
import { CustomFieldEditor } from './CustomFieldEditor';
import { TimelinePanel } from './TimelinePanel';
import { ImportDialog } from './ImportDialog';
import { DedupDialog } from './DedupDialog';

const LIFECYCLE_STAGES = [
  { value: 'subscriber', label: 'Subscriber' },
  { value: 'lead', label: 'Lead' },
  { value: 'mql', label: 'MQL' },
  { value: 'sql', label: 'SQL' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'customer', label: 'Customer' },
  { value: 'evangelist', label: 'Evangelist' },
  { value: 'other', label: 'Other' },
];

const STAGE_COLORS: Record<string, string> = {
  subscriber: 'bg-gray-100 text-gray-700',
  lead: 'bg-blue-100 text-blue-700',
  mql: 'bg-indigo-100 text-indigo-700',
  sql: 'bg-purple-100 text-purple-700',
  opportunity: 'bg-amber-100 text-amber-700',
  customer: 'bg-green-100 text-green-700',
  evangelist: 'bg-emerald-100 text-emerald-700',
  other: 'bg-gray-100 text-gray-700',
};

const emptyForm: LeadsCreateContactInput = {
  name: '',
  email: '',
  phone: '',
  title: '',
  department: '',
  source: '',
  lifecycleStage: 'lead',
  notes: '',
  tags: [],
};

export function ContactsView() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<LeadsContact | null>(null);
  const [form, setForm] = useState<LeadsCreateContactInput>({ ...emptyForm });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showDedup, setShowDedup] = useState(false);

  const { contacts, total, loading, createContact, updateContact, deleteContact, archiveContact } =
    useContacts({ search: debouncedSearch, sortBy: 'name', sortDir: 'asc' });

  const { companies } = useCompanies({ limit: 200 });
  const { exportEntities, exporting } = useImportExport();
  const { scoreLead, generateSummary, loading: aiLoading } = useLeadsAI();
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    bulkDeleteContacts,
    bulkArchiveContacts,
    bulkUpdateContacts,
    loading: bulkLoading,
  } = useBulkActions();

  // Debounce search
  useMemo(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  function openCreate() {
    setEditingContact(null);
    setForm({ ...emptyForm });
    setTagInput('');
    setShowForm(true);
  }

  function openEdit(contact: LeadsContact) {
    setEditingContact(contact);
    setForm({
      companyId: contact.companyId ?? undefined,
      name: contact.name,
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      title: contact.title ?? '',
      department: contact.department ?? '',
      source: contact.source ?? '',
      lifecycleStage: contact.lifecycleStage,
      notes: contact.notes ?? '',
      tags: contact.tags ?? [],
      metadata: contact.metadata ?? {},
    });
    setTagInput('');
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast({
        title: 'Validation',
        description: 'Contact name is required',
        variant: 'destructive',
      });
      return;
    }
    setSaving(true);
    try {
      if (editingContact) {
        await updateContact(editingContact.id, form);
        toast({ title: 'Contact updated' });
      } else {
        await createContact(form);
        toast({ title: 'Contact created' });
      }
      setShowForm(false);
    } catch {
      toast({ title: 'Error', description: 'Failed to save contact', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(contact: LeadsContact) {
    try {
      await deleteContact(contact.id);
      toast({ title: 'Contact moved to trash' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete contact', variant: 'destructive' });
    }
  }

  async function handleArchive(contact: LeadsContact) {
    try {
      await archiveContact(contact.id);
      toast({ title: contact.status === 'archived' ? 'Contact unarchived' : 'Contact archived' });
    } catch {
      toast({ title: 'Error', description: 'Failed to archive contact', variant: 'destructive' });
    }
  }

  function handleTagKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!form.tags?.includes(tagInput.trim())) {
        setForm({ ...form, tags: [...(form.tags ?? []), tagInput.trim()] });
      }
      setTagInput('');
    }
  }

  function removeTag(tag: string) {
    setForm({ ...form, tags: form.tags?.filter((t) => t !== tag) });
  }

  return (
    <div>
      {/* Bulk Action Bar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 px-6 py-3 bg-primary/5 border-b">
          <Checkbox
            checked={selectedCount === contacts.length}
            onCheckedChange={(checked) => {
              if (checked) selectAll(contacts.map((c) => c.id));
              else clearSelection();
            }}
          />
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <div className="flex items-center gap-2 ml-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={bulkLoading}>
                  Set Lifecycle
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {LIFECYCLE_STAGES.map((s) => (
                  <DropdownMenuItem
                    key={s.value}
                    onClick={() => bulkUpdateContacts({ lifecycleStage: s.value })}
                  >
                    {s.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={() => bulkArchiveContacts()}
              disabled={bulkLoading}
            >
              <Archive className="h-4 w-4 mr-1" />
              Archive
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => bulkDeleteContacts()}
              disabled={bulkLoading}
              className="text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={clearSelection}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6 px-6 pt-6">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search contacts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <span className="text-sm text-muted-foreground">{total} contacts</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowDedup(true)}>
            <Copy className="h-4 w-4 mr-2" />
            Find Duplicates
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportEntities('contacts')}
            disabled={exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Contact List */}
      {loading ? (
        <div className="grid gap-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No contacts yet</p>
          <p className="text-sm mt-1">Add your first contact to get started</p>
          <Button variant="outline" className="mt-4" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {contacts.map((contact) => (
            <Card key={contact.id} className="hover:bg-muted/30 transition-colors">
              <CardContent className="p-4 flex items-center justify-between">
                <Checkbox
                  checked={selectedIds.has(contact.id)}
                  onCheckedChange={() => toggleSelect(contact.id)}
                  className="mr-3 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{contact.name}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        STAGE_COLORS[contact.lifecycleStage] ?? STAGE_COLORS.other
                      }`}
                    >
                      {LIFECYCLE_STAGES.find((s) => s.value === contact.lifecycleStage)?.label ??
                        contact.lifecycleStage}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1.5 text-sm text-muted-foreground">
                    {contact.title && (
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-3 w-3" />
                        {contact.title}
                      </span>
                    )}
                    {contact.companyName && (
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {contact.companyName}
                      </span>
                    )}
                    {contact.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {contact.email}
                      </span>
                    )}
                    {contact.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {contact.phone}
                      </span>
                    )}
                  </div>
                  {contact.tags.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {contact.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 ml-4">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(contact)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleArchive(contact)}>
                      <Archive className="h-4 w-4 mr-2" />
                      Archive
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => scoreLead(contact.id)} disabled={aiLoading}>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Score Lead
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => generateSummary('contact', contact.id)}
                      disabled={aiLoading}
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI Summary
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDelete(contact)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className={editingContact ? 'sm:max-w-3xl' : 'sm:max-w-lg'}>
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid gap-2">
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="contact-phone">Phone</Label>
                <Input
                  id="contact-phone"
                  value={form.phone ?? ''}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="contact-title">Title</Label>
                <Input
                  id="contact-title"
                  value={form.title ?? ''}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="VP of Sales"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="contact-department">Department</Label>
                <Input
                  id="contact-department"
                  value={form.department ?? ''}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Company</Label>
                <Select
                  value={form.companyId ?? ''}
                  onValueChange={(v) => setForm({ ...form, companyId: v || undefined })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Lifecycle Stage</Label>
                <Select
                  value={form.lifecycleStage ?? 'lead'}
                  onValueChange={(v) => setForm({ ...form, lifecycleStage: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LIFECYCLE_STAGES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-source">Source</Label>
              <Input
                id="contact-source"
                value={form.source ?? ''}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                placeholder="LinkedIn, Referral, Website..."
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="contact-notes">Notes</Label>
              <Textarea
                id="contact-notes"
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-1 mb-1">
                {form.tags?.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => removeTag(tag)}
                  >
                    {tag} &times;
                  </Badge>
                ))}
              </div>
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Type tag and press Enter"
              />
            </div>
          </div>

          <CustomFieldEditor
            entityType="contact"
            metadata={form.metadata ?? {}}
            onChange={(metadata) => setForm({ ...form, metadata })}
          />

          {editingContact && (
            <div className="border-t pt-4 mt-2">
              <TimelinePanel contactId={editingContact.id} limit={10} />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingContact ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        entityType="contacts"
        onImportComplete={() => {
          setShowImport(false);
        }}
      />

      <DedupDialog open={showDedup} onOpenChange={setShowDedup} />
    </div>
  );
}
